// Positional game-audio primitives. All game SFX route through here.
//
// Structure:
//   AudioBus           — listener + scene anchor + optional shared reverb bus.
//                        Owns three GainNodes (master / music / sfx) sitting
//                        between every source and listener.getInput(). User
//                        volume sliders write to these buses; per-source
//                        volume stays at the map's natural level. Reverb wet
//                        feeds the SFX bus, so reverb tail follows SFX volume.
//   PositionalOneShot  — round-robin pool for short samples (footsteps, hits,
//                        swings). Routed through SFX bus.
//   PositionalLoop     — single looping source parented to any Object3D
//                        (used for the continuous ion-thruster rumble).
//                        Routed through SFX bus.
//   Music              — non-positional looping stream for OST tracks.
//                        Routed through Music bus, bypassing reverb.
//   AmbientLoop        — non-positional looping bed (ocean, ship-creak,
//                        sink-progress). Routed through SFX bus.
//
// Dry / wet are parallel paths per source. Adjusting a source's DRY_GAIN
// doesn't drag its reverb level; turning reverb off at the bus level doesn't
// affect the dry mix.

import * as THREE from 'three';
import { CONFIG } from '../config';

// One-time runtime patch: wrap PositionalAudio.updateMatrixWorld so a
// non-finite matrix (typically from a parent group whose quaternion went
// NaN) doesn't blow up the renderer on every frame. Web Audio's
// AudioParam.linearRampToValueAtTime throws synchronously on a non-finite
// value and there's no try/catch in three.js's internal traversal — one
// bad node wedges the entire scene graph.
//
// When the patch catches a non-finite value it logs once-per-node with the
// node's name, world position, world quaternion, and (best-effort)
// immediate parent chain, so we can trace back to whichever ancestor
// matrix went bad. After the first log per node we stay quiet so the
// console doesn't flood.
const __loggedNodeIds = new WeakSet<object>();
// Install at module load so the patch is active even if a PositionalAudio
// is ever created before the first AudioBus. Idempotent via the check on
// the prototype's own sentinel.
(function installPositionalAudioGuard(): void {
  const proto = THREE.PositionalAudio.prototype as THREE.PositionalAudio & { __finitePatched?: boolean };
  if (proto.__finitePatched) return;
  proto.__finitePatched = true;
  console.log('[GameAudio] PositionalAudio.updateMatrixWorld guard installed');
  proto.updateMatrixWorld = function patchedUpdateMatrixWorld(this: THREE.PositionalAudio, force: boolean): void {
    THREE.Object3D.prototype.updateMatrixWorld.call(this, force);
    const self = this as unknown as {
      hasPlaybackControl: boolean;
      isPlaying: boolean;
      panner: PannerNode;
      listener: THREE.AudioListener;
      context: BaseAudioContext;
      matrixWorld: THREE.Matrix4;
    };
    if (self.hasPlaybackControl && self.isPlaying === false) return;
    const p = new THREE.Vector3();
    const q = new THREE.Quaternion();
    const s = new THREE.Vector3();
    self.matrixWorld.decompose(p, q, s);
    const orient = new THREE.Vector3(0, 0, 1).applyQuaternion(q);
    const panner = self.panner;
    const listener = self.listener;
    const ctx = self.context;
    // Coerce non-finite values to 0 so we never hand WebAudio a NaN. On
    // first-time bad values per node, dump the full state so we can trace
    // the real upstream source without the render loop ever dying.
    const rawPx = p.x, rawPy = p.y, rawPz = p.z;
    const rawOx = orient.x, rawOy = orient.y, rawOz = orient.z;
    const rawTD = listener.timeDelta;
    const rawCT = ctx.currentTime;
    const safe = (v: number): number => Number.isFinite(v) ? v : 0;
    const sx = safe(rawPx), sy = safe(rawPy), sz = safe(rawPz);
    const ox = safe(rawOx), oy = safe(rawOy), oz = safe(rawOz);
    const td = safe(rawTD);
    const endTime = rawCT + td;
    const finalEnd = Number.isFinite(endTime) ? endTime : rawCT;
    const anyBad =
      sx !== rawPx || sy !== rawPy || sz !== rawPz ||
      ox !== rawOx || oy !== rawOy || oz !== rawOz ||
      td !== rawTD || finalEnd !== endTime;
    if (anyBad) {
      const key = this as unknown as object;
      if (!__loggedNodeIds.has(key)) {
        __loggedNodeIds.add(key);
        const chain: string[] = [];
        let cur: THREE.Object3D | null = this;
        while (cur) {
          chain.push(`${cur.constructor.name}${cur.name ? `(${cur.name})` : ''}`);
          cur = cur.parent;
        }
        // Also dump the parent's world matrix elements — if the NaN came
        // from an ancestor, these will be non-finite even though the node's
        // own local position is fine.
        const parentWorld = (this.parent && (this.parent as THREE.Object3D).matrixWorld.elements) ?? null;
        console.error('[GameAudio] PositionalAudio update saw non-finite input — clamping to 0 to keep renderer alive', {
          raw: { pos: { x: rawPx, y: rawPy, z: rawPz }, orient: { x: rawOx, y: rawOy, z: rawOz }, timeDelta: rawTD, ctxCurrentTime: rawCT, endTime },
          clamped: { pos: { x: sx, y: sy, z: sz }, orient: { x: ox, y: oy, z: oz }, endTime: finalEnd },
          nodeName: this.name,
          nodePosition: { x: this.position.x, y: this.position.y, z: this.position.z },
          parentName: this.parent?.name ?? null,
          parentClass: this.parent?.constructor.name ?? null,
          parentMatrixWorld: parentWorld ? Array.from(parentWorld) : null,
          parentChain: chain.join(' <- ')
        });
      }
    }
    // Call each ramp independently so one bad AudioParam doesn't prevent
    // the others from advancing. try/catch is belt-and-braces — with the
    // clamping above, every value going in is finite, but if WebAudio ever
    // disagrees about the SAME value being finite (e.g. subnormals it
    // rejects) we still keep rendering.
    const ramp = (param: AudioParam, v: number): void => {
      try { param.linearRampToValueAtTime(v, finalEnd); } catch { /* swallow */ }
    };
    ramp(panner.positionX, sx);
    ramp(panner.positionY, sy);
    ramp(panner.positionZ, sz);
    ramp(panner.orientationX, ox);
    ramp(panner.orientationY, oy);
    ramp(panner.orientationZ, oz);
  };
})();

// ---------------------------------------------------------------------------
// Reverb shape (IR curve). Shared across the whole bus when reverb is on.
// Per-source wet send level is controlled by each source's own `wetGain`
// option — that gain node sits between the source's panner and the shared
// convolver, so tuning one source's reverb contribution doesn't change any
// others' and is fully independent of that source's dry volume.

const DEFAULT_REVERB_DURATION_SEC = 4;
const DEFAULT_REVERB_IR_AMPLITUDE = 0.3;
const DEFAULT_REVERB_DECAY = 2.6;

export interface ReverbConfig {
  /** Optional impulse-response audio file. When set, the decoded buffer is
   *  used as the convolver's IR and the procedural fields are ignored. The
   *  bus uses a generated IR until the file finishes loading; if loading
   *  fails the generated IR is kept. */
  irUrl?: string;
  /** Global multiplier on the wet bus (0..). Scales every source's wet send
   *  uniformly without touching their individual `wetGain` values. Default 1. */
  wet?: number;
  durationSec?: number;
  decay?: number;
  amplitude?: number;
}

function buildImpulseResponse(ctx: BaseAudioContext, cfg: ReverbConfig | null | undefined): AudioBuffer {
  const duration = cfg?.durationSec ?? DEFAULT_REVERB_DURATION_SEC;
  const decay = cfg?.decay ?? DEFAULT_REVERB_DECAY;
  const amplitude = cfg?.amplitude ?? DEFAULT_REVERB_IR_AMPLITUDE;
  const sampleRate = ctx.sampleRate;
  const length = Math.max(1, Math.floor(duration * sampleRate));
  const ir = ctx.createBuffer(2, length, sampleRate);
  for (let ch = 0; ch < 2; ch++) {
    const data = ir.getChannelData(ch);
    for (let i = 0; i < length; i++) {
      const t = i / length;
      const falloff = Math.pow(1 - t, decay);
      data[i] = (Math.random() * 2 - 1) * falloff * amplitude;
    }
  }
  return ir;
}

// ---------------------------------------------------------------------------

export class AudioBus {
  readonly listener: THREE.AudioListener;
  readonly scene: THREE.Scene;
  /** Group parented to the scene — all pooled PositionalAudio nodes live
   *  under this so their matrixWorld updates each render frame. */
  readonly anchor: THREE.Group;
  /** Master gain. Every audio path (music + sfx + reverb wet) feeds in
   *  here, and this terminal node connects to listener.getInput(). Drives
   *  the user's "Master" volume slider. */
  private readonly masterGain: GainNode;
  /** Music sub-bus. Music sources route their final gain into this. Drives
   *  the user's "Music" volume slider. */
  private readonly musicGain: GainNode;
  /** SFX sub-bus. PositionalOneShot / PositionalLoop / AmbientLoop sources
   *  AND the shared reverb wet path all feed into this. Drives the user's
   *  "SFX" volume slider — reverb tail tracks SFX volume because it's
   *  perceptually part of the SFX, not music. */
  private readonly sfxGain: GainNode;
  /** Shared convolver. Every source that wants reverb routes a per-connection
   *  gain node into this, so all SFX share one IR (consistent tail colour)
   *  but each controls its own send level. Null when reverb is disabled. */
  private readonly convolver: ConvolverNode | null;
  /** Global wet trim sitting between the convolver and the SFX bus. Lets
   *  the map scale every source's reverb contribution at once without
   *  touching individual wetGain values. Null when reverb is disabled. */
  private readonly wetGain: GainNode | null;
  /** One BiquadFilterNode (lowpass) per registered source, inserted into
   *  that source's chain via THREE.Audio.setFilter(). A shared AudioParam
   *  automation would be nicer, but three.js wants the filter instance per
   *  source — so we keep refs here and move them all in lockstep when
   *  setFilterCutoff() is called. Covers both dry and wet (reverb) paths
   *  since the filter sits before the panner in the source's internal
   *  chain, and the wet send taps the panner output. */
  static readonly BYPASS_HZ = 20000;
  private readonly filters: BiquadFilterNode[] = [];
  private filterCutoffHz = AudioBus.BYPASS_HZ;
  /** Live registry of every constructed bus. The settings layer walks this
   *  to broadcast user volume changes to the menu bus AND any active match
   *  bus simultaneously. dispose() removes the bus on teardown so the
   *  registry doesn't leak across match cycles. */
  private static readonly liveBuses = new Set<AudioBus>();
  private disposed = false;

  constructor(
    listener: THREE.AudioListener,
    scene: THREE.Scene,
    reverb: ReverbConfig | null
  ) {
    // The PositionalAudio.updateMatrixWorld guard is installed via the
    // module-top IIFE above; no per-instance hook needed here.
    this.listener = listener;
    this.scene = scene;
    this.anchor = new THREE.Group();
    this.anchor.name = 'GameAudioPool';
    this.scene.add(this.anchor);

    const ctx = listener.context;
    // Build the master / music / sfx chain. Initial values come from
    // CONFIG.audio so a bus constructed AFTER applyAudioSettings has already
    // run picks up the user's saved volumes. (For the menu bus, which is
    // constructed at module load before applyAudioSettings, the defaults
    // are CONFIG.audio's startup values — applyAudioSettings then ramps
    // each live bus to the user's saved values.)
    this.masterGain = ctx.createGain();
    this.masterGain.gain.value = CONFIG.audio.masterVolume;
    this.masterGain.connect(listener.getInput());
    this.musicGain = ctx.createGain();
    this.musicGain.gain.value = CONFIG.audio.musicVolume;
    this.musicGain.connect(this.masterGain);
    this.sfxGain = ctx.createGain();
    this.sfxGain.gain.value = CONFIG.audio.sfxVolume;
    this.sfxGain.connect(this.masterGain);

    if (reverb) {
      const convolver = ctx.createConvolver();
      // Always seed with a generated IR so the bus has audible reverb from
      // frame zero, even when an irUrl is configured. The fetched buffer
      // overwrites this once decoded; on load failure we keep the generated
      // tail rather than going silent.
      convolver.buffer = buildImpulseResponse(ctx, reverb);
      const wet = ctx.createGain();
      wet.gain.value = reverb.wet ?? 1;
      convolver.connect(wet);
      // Reverb tail rides SFX volume — reverb is perceptually part of the
      // SFX, not a separate channel. Master scales it once more on top.
      wet.connect(this.sfxGain);
      this.convolver = convolver;
      this.wetGain = wet;
      if (reverb.irUrl) {
        const url = reverb.irUrl;
        const loader = new THREE.AudioLoader();
        loader.load(
          url,
          (buf) => { convolver.buffer = buf; },
          undefined,
          (err) => { console.warn('[GameAudio] failed to load reverb IR', url, err); }
        );
      }
    } else {
      this.convolver = null;
      this.wetGain = null;
    }

    AudioBus.liveBuses.add(this);
  }

  /** Tear down: disconnect the bus chain and drop from the live registry.
   *  The match-side AudioBus calls this from FxBus.dispose so old buses
   *  don't keep receiving setMaster/Music/SfxVolume broadcasts after the
   *  match ends. Idempotent. */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    AudioBus.liveBuses.delete(this);
    try { this.masterGain.disconnect(); } catch { /* ignore */ }
    try { this.musicGain.disconnect(); } catch { /* ignore */ }
    try { this.sfxGain.disconnect(); } catch { /* ignore */ }
    if (this.wetGain) try { this.wetGain.disconnect(); } catch { /* ignore */ }
    if (this.convolver) try { this.convolver.disconnect(); } catch { /* ignore */ }
  }

  /** Snapshot of every currently-alive bus. The settings layer iterates
   *  this to broadcast volume changes — both menu music and match SFX
   *  follow a single slider drag. */
  static getLiveBuses(): AudioBus[] {
    return Array.from(AudioBus.liveBuses);
  }

  /** Smooth ramps for the three user-facing volume sliders. 10ms time
   *  constant matches the existing setFilterCutoff smoothing — fast enough
   *  to feel instant under a slider drag, slow enough to avoid zipper
   *  noise from per-frame writes. */
  setMasterVolume(v: number): void {
    if (this.disposed) return;
    const t = this.listener.context.currentTime;
    this.masterGain.gain.setTargetAtTime(Math.max(0, v), t, 0.01);
  }
  setMusicVolume(v: number): void {
    if (this.disposed) return;
    const t = this.listener.context.currentTime;
    this.musicGain.gain.setTargetAtTime(Math.max(0, v), t, 0.01);
  }
  setSfxVolume(v: number): void {
    if (this.disposed) return;
    const t = this.listener.context.currentTime;
    this.sfxGain.gain.setTargetAtTime(Math.max(0, v), t, 0.01);
  }

  /** Reroute a THREE.Audio / PositionalAudio's terminal gain into the SFX
   *  sub-bus instead of listener.getInput(). Call AFTER setBuffer +
   *  registerNode so the filter chain is in place — the filter sits
   *  between the source and the gain, so rerouting the gain doesn't
   *  disturb it. */
  attachSourceToSfx(node: THREE.Audio<AudioNode>): void {
    const g = (node as unknown as { gain: GainNode }).gain;
    try { g.disconnect(); } catch { /* ignore */ }
    g.connect(this.sfxGain);
  }

  /** Reroute a THREE.Audio's terminal gain into the Music sub-bus.
   *  Music sources are non-positional (THREE.Audio, not PositionalAudio)
   *  and bypass reverb — they're not part of the SFX mix. */
  attachSourceToMusic(node: THREE.Audio<AudioNode>): void {
    const g = (node as unknown as { gain: GainNode }).gain;
    try { g.disconnect(); } catch { /* ignore */ }
    g.connect(this.musicGain);
  }

  /** Connect a raw Web Audio node into the SFX sub-bus. Used by sources
   *  that aren't wrapped in a THREE.Audio (e.g. the tutorial narrator,
   *  which streams through a custom HTMLAudioElement → MediaElementSource
   *  → bandpass chain). The narrator's terminal gain calls this so the
   *  user's SFX volume slider applies to the voice line just like any
   *  other in-game sound. */
  connectRawToSfx(node: AudioNode): void {
    node.connect(this.sfxGain);
  }

  /** AudioContext owned by the listener — exposed so external graphs can
   *  build their own nodes in the same context (mandatory: nodes from
   *  different contexts can't be connected). The narrator engine builds
   *  its bandpass + convolver chain in this context so it can hand the
   *  output to connectRawToSfx. */
  get context(): AudioContext {
    return this.listener.context as AudioContext;
  }

  /** Scale every source's wet contribution at once. No-op on dry buses. */
  setReverbWet(level: number): void {
    if (!this.wetGain) return;
    const t = this.listener.context.currentTime;
    this.wetGain.gain.setTargetAtTime(Math.max(0, level), t, 0.01);
  }

  /** Register a source so the bus can modulate its lowpass later. Creates a
   *  BiquadFilterNode, installs it on the source via setFilter(), and tracks
   *  it. Setting Q at Butterworth (0.707) keeps the passband flat and
   *  resonance-free so the filter reads as a darkening rather than a
   *  coloured sweep. */
  registerNode(node: THREE.Audio<AudioNode>): void {
    const ctx = this.listener.context;
    const f = ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.Q.value = 0.707;
    f.frequency.value = this.filterCutoffHz;
    node.setFilter(f);
    this.filters.push(f);
  }

  /** Move every source's lowpass cutoff to `hz`. At BYPASS_HZ the filter is
   *  effectively transparent. Skips trivially-close values so we don't
   *  thrash the audio graph when the cutoff is already at its target. */
  setFilterCutoff(hz: number): void {
    if (Math.abs(hz - this.filterCutoffHz) < 1) return;
    this.filterCutoffHz = hz;
    const t = this.listener.context.currentTime;
    // Short exponential target smooths zipper noise from per-frame writes
    // without feeling laggy (10ms). Time constant << any ramp we care about.
    for (const f of this.filters) f.frequency.setTargetAtTime(hz, t, 0.01);
  }

  /** Audio-thread exponential ramp on the lowpass cutoff over `durationSec`.
   *  Use this for multi-second transitions (menu-into-game sweep, etc.) — the
   *  per-frame `setTargetAtTime` variant has a 10ms time constant and would
   *  land almost instantly. Cancels any previously scheduled values on each
   *  filter so rapid re-targets don't compound. exponentialRampToValueAtTime
   *  cannot cross zero, so the target is clamped to ≥1 Hz. */
  rampFilterCutoff(hz: number, durationSec: number): void {
    if (Math.abs(hz - this.filterCutoffHz) < 1) return;
    this.filterCutoffHz = hz;
    const t = this.listener.context.currentTime;
    const target = Math.max(1, hz);
    for (const f of this.filters) {
      f.frequency.cancelScheduledValues(t);
      f.frequency.setValueAtTime(f.frequency.value, t);
      f.frequency.exponentialRampToValueAtTime(target, t + durationSec);
    }
  }

  /** Route the post-panner stereo output of a positional source into the
   *  shared reverb, at `wetLevel` amplitude (0..1 typical — 0 means "send no
   *  reverb for this source"). No-op when reverb is disabled on this bus.
   *  Each call creates its own GainNode, so sources with multiple pool nodes
   *  end up with one gain per panner — all at the same level. */
  connectWet(panner: PannerNode, wetLevel: number): void {
    if (!this.convolver || wetLevel <= 0) return;
    const ctx = this.listener.context;
    const g = ctx.createGain();
    g.gain.value = wetLevel;
    panner.connect(g);
    g.connect(this.convolver);
  }

  /** Resume the AudioContext if the browser suspended it before first gesture. */
  resumeIfSuspended(): void {
    const ctx = this.listener.context;
    if (ctx.state === 'suspended') void ctx.resume();
  }
}

// ---------------------------------------------------------------------------

interface OneShotOpts {
  /** How many PositionalAudio nodes to allocate. Size so overlapping plays
   *  don't cut each other off — 1 per expected simultaneous voice + a little
   *  headroom. Samples are short so slots cycle quickly. */
  poolSize: number;
  /** Per-source dry level (0..1). Independent of wet. */
  dryGain: number;
  /** Per-source reverb send level (0..1). 0 means no reverb for this source
   *  even when the bus has reverb enabled. Ignored entirely on buses with
   *  reverb disabled. Independent of dry. */
  wetGain: number;
  refDistance?: number;
  rolloffFactor?: number;
  maxDistance?: number;
}

export class PositionalOneShot {
  private readonly bus: AudioBus;
  private readonly urls: string[];
  private buffers: AudioBuffer[] = [];
  private pool: THREE.PositionalAudio[] = [];
  private nextBuffer = 0;
  private nextNode = 0;

  constructor(bus: AudioBus, urls: string[], opts: OneShotOpts) {
    this.bus = bus;
    this.urls = urls;

    for (let i = 0; i < opts.poolSize; i++) {
      const a = new THREE.PositionalAudio(bus.listener);
      a.setRefDistance(opts.refDistance ?? 3);
      a.setRolloffFactor(opts.rolloffFactor ?? 1);
      a.setDistanceModel('inverse');
      a.setMaxDistance(opts.maxDistance ?? 120);
      a.setVolume(opts.dryGain);
      bus.connectWet(a.panner, opts.wetGain);
      bus.anchor.add(a);
      bus.registerNode(a);
      bus.attachSourceToSfx(a);
      this.pool.push(a);
    }

    void this.loadAll();
  }

  private async loadAll(): Promise<void> {
    const loader = new THREE.AudioLoader();
    const load = (url: string) => new Promise<AudioBuffer>((resolve, reject) => {
      loader.load(url, (buf) => resolve(buf), undefined, (err) => reject(err));
    });
    try {
      this.buffers = await Promise.all(this.urls.map(load));
    } catch (err) {
      console.warn('[GameAudio] failed to load samples', this.urls, err);
    }
  }

  /** Play the next sample at world-space (x, y, z). No-op until buffers load. */
  play(x: number, y: number, z: number): void {
    if (this.buffers.length === 0) return;
    // Web Audio's AudioParam.linearRampToValueAtTime throws on a non-finite
    // value, which is what PositionalAudio.updateMatrixWorld feeds the panner
    // every frame while the node is playing. One bad sample position wedges
    // the entire render loop with "provided float value is non-finite".
    // JSON round-trips turn NaN into null (which behaves as NaN here too)
    // when events travel server→client, so we defend at the play callsite.
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) {
      console.warn('[GameAudio] dropping one-shot at non-finite position', { x, y, z });
      return;
    }
    this.bus.resumeIfSuspended();

    const buffer = this.buffers[this.nextBuffer]!;
    this.nextBuffer = (this.nextBuffer + 1) % this.buffers.length;

    const node = this.pool[this.nextNode]!;
    this.nextNode = (this.nextNode + 1) % this.pool.length;

    if (node.isPlaying) node.stop();
    node.setBuffer(buffer);
    node.position.set(x, y, z);
    node.play();
  }
}

// ---------------------------------------------------------------------------

interface LoopOpts {
  /** Per-source dry level (0..1). Independent of wet. */
  dryGain: number;
  /** Per-source reverb send level (0..1). 0 = no reverb for this source.
   *  Ignored on buses with reverb disabled. Independent of dry. */
  wetGain: number;
  refDistance?: number;
  rolloffFactor?: number;
  maxDistance?: number;
}

export class PositionalLoop {
  private readonly bus: AudioBus;
  private readonly node: THREE.PositionalAudio;
  private readonly url: string;
  private loaded = false;
  private wantsPlaying = false;

  constructor(bus: AudioBus, url: string, opts: LoopOpts) {
    this.bus = bus;
    this.url = url;
    this.node = new THREE.PositionalAudio(bus.listener);
    this.node.setRefDistance(opts.refDistance ?? 6);
    this.node.setRolloffFactor(opts.rolloffFactor ?? 1);
    this.node.setDistanceModel('inverse');
    this.node.setMaxDistance(opts.maxDistance ?? 160);
    this.node.setVolume(opts.dryGain);
    this.node.setLoop(true);
    bus.connectWet(this.node.panner, opts.wetGain);
    bus.registerNode(this.node);
    bus.attachSourceToSfx(this.node);

    void this.load();
  }

  /** Parent the audio source under any Object3D (e.g. the ship group) and
   *  set its local-space position. Matrix updates flow via the scene graph,
   *  so the source banks with its parent for free. */
  attachTo(parent: THREE.Object3D, x: number, y: number, z: number): void {
    // See GameAudio.play() — non-finite positions crash the panner param.
    // attachTo only runs at setup so this is defensive against config
    // regressions rather than wire noise.
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) {
      console.warn('[GameAudio] loop attachTo called with non-finite position', { x, y, z });
      return;
    }
    parent.add(this.node);
    this.node.position.set(x, y, z);
  }

  start(): void {
    this.wantsPlaying = true;
    this.tryStart();
  }

  stop(): void {
    this.wantsPlaying = false;
    if (this.node.isPlaying) this.node.stop();
  }

  /** Update dry volume. Used for the back-to-lobby fade-out ramp so the
   *  thruster loop doesn't keep rumbling after the game canvas has been
   *  torn down. */
  setVolume(v: number): void {
    this.node.setVolume(v);
  }

  private async load(): Promise<void> {
    const loader = new THREE.AudioLoader();
    try {
      const buffer = await new Promise<AudioBuffer>((resolve, reject) => {
        loader.load(this.url, (buf) => resolve(buf), undefined, (err) => reject(err));
      });
      this.node.setBuffer(buffer);
      this.loaded = true;
      this.tryStart();
    } catch (err) {
      console.warn('[GameAudio] failed to load loop', this.url, err);
    }
  }

  private tryStart(): void {
    if (!this.loaded || !this.wantsPlaying || this.node.isPlaying) return;
    this.bus.resumeIfSuspended();
    this.node.play();
  }
}

// ---------------------------------------------------------------------------

export class Music {
  private readonly bus: AudioBus;
  private readonly node: THREE.Audio;
  private loaded = false;
  private wantsPlaying = false;

  constructor(bus: AudioBus, url: string, volume: number) {
    this.bus = bus;
    // Non-positional THREE.Audio routed through the bus's Music sub-bus.
    // Per-source volume stays at the map's natural level; the user-facing
    // music volume lives on bus.musicGain, applied uniformly across every
    // music source on every active bus.
    this.node = new THREE.Audio(bus.listener);
    this.node.setLoop(true);
    this.node.setVolume(volume);
    bus.registerNode(this.node);
    bus.attachSourceToMusic(this.node);
    void this.load(url);
  }

  setVolume(v: number): void {
    this.node.setVolume(v);
  }

  /** Current per-source gain. Used by transient ramps (back-to-lobby fade)
   *  that need to start from the source's natural level rather than a
   *  cached number. */
  getVolume(): number {
    return this.node.getVolume();
  }

  start(): void {
    this.wantsPlaying = true;
    this.tryStart();
  }

  stop(): void {
    this.wantsPlaying = false;
    if (this.node.isPlaying) this.node.stop();
  }

  private async load(url: string): Promise<void> {
    const loader = new THREE.AudioLoader();
    try {
      const buffer = await new Promise<AudioBuffer>((resolve, reject) => {
        loader.load(url, (b) => resolve(b), undefined, (e) => reject(e));
      });
      this.node.setBuffer(buffer);
      this.loaded = true;
      this.tryStart();
    } catch (err) {
      console.warn('[GameAudio] failed to load music', url, err);
    }
  }

  private tryStart(): void {
    if (!this.loaded || !this.wantsPlaying || this.node.isPlaying) return;
    this.bus.resumeIfSuspended();
    this.node.play();
  }
}

// ---------------------------------------------------------------------------

/** Non-positional looping background source. Same Web Audio routing as
 *  Music (THREE.Audio → listener.getInput()) — no panner, no reverb send.
 *  Used for ambient beds (ocean, ship-creak) and the sink-progress loop
 *  whose gain is updated each render frame from a behavior accessor. */
export class AmbientLoop {
  private readonly bus: AudioBus;
  private readonly node: THREE.Audio;
  private loaded = false;
  private wantsPlaying = false;

  constructor(bus: AudioBus, url: string, volume: number) {
    this.bus = bus;
    this.node = new THREE.Audio(bus.listener);
    this.node.setLoop(true);
    this.node.setVolume(volume);
    bus.registerNode(this.node);
    bus.attachSourceToSfx(this.node);
    void this.load(url);
  }

  setVolume(v: number): void {
    this.node.setVolume(v);
  }

  start(): void {
    this.wantsPlaying = true;
    this.tryStartLoop();
  }

  stop(): void {
    this.wantsPlaying = false;
    if (this.node.isPlaying) this.node.stop();
  }

  private async load(url: string): Promise<void> {
    const loader = new THREE.AudioLoader();
    try {
      const buffer = await new Promise<AudioBuffer>((resolve, reject) => {
        loader.load(url, (b) => resolve(b), undefined, (e) => reject(e));
      });
      this.node.setBuffer(buffer);
      this.loaded = true;
      this.tryStartLoop();
    } catch (err) {
      console.warn('[GameAudio] failed to load ambient loop', url, err);
    }
  }

  private tryStartLoop(): void {
    if (!this.loaded || !this.wantsPlaying || this.node.isPlaying) return;
    this.bus.resumeIfSuspended();
    this.node.play();
  }
}
